import { Feather } from '@expo/vector-icons';
import {
  FormElementMeta,
  FormGridConfig,
  FormOptionsConfig,
  FormValueLabel
} from '@sierralabs/forms-core';
import React, { useMemo, useRef, useState } from 'react';
import { Platform, TextInputProps, TouchableOpacity, View } from 'react-native';
import styled from 'styled-components/native';
import { B } from '../../../components/Typography';
import Colors from '../../../constants/Colors';
import { useDynamicFormContext } from '../../DynamicFormContext';
import { FormElementProps } from '../../FormElement';
import { FormElementPlugin } from '../FormElementPlugin';
import FormElementQuestion from './components/FormElementQuestion';
import { validateFormOtherFieldValue } from '../../../common/validateFormOtherFieldValue';

const StyledContainer = styled.View<{ direction?: 'row' | 'column' }>`
  flex-direction: ${p => p.direction || 'row'};
  height: auto;
  min-height: 53px;
  width: 100%;
  background-color: #fff;
  border-radius: 8px;
  padding: 16px;
  border: 1px solid ${Colors.formInputBorder};
  align-items: center;
`;
const StyledRow = styled.View`
  width: 100%;
  flex-direction: row;
  align-items: center;
`;

const StyledTouchableOpacity = styled(TouchableOpacity)`
  margin-top: 16px;
`;

const StyledBox = styled.View<{ touched?: boolean }>`
  width: 24px;
  height: 24px;
  border-radius: 6px;
  border: 1px solid ${Colors.checkboxTouched};
  background-color: ${p => (p.touched ? Colors.checkboxTouched : '#fff')};
  align-items: center;
  justify-content: center;
  margin-right: 16px;
  box-shadow: none;
  align-self: center;
`;

const StyledB = styled(B)`
  letter-spacing: 0.09px;
  line-height: 18px;
  flex: 1 1 auto;
`;
const StyledOtherLabel = styled(B)`
  letter-spacing: 0.09px;
  line-height: 18px;
  margin-right: 16px;
  flex: 1 1 auto;
  flex-shrink: 1;
`;
const StyledTextInput = styled.TextInput<{
  hasHelperText?: boolean;
  minHeight?: number;
  multiline?: boolean;
  error?: boolean;
  empty?: boolean;
}>`
  font-family: ${p => (p.empty ? 'WorkSans-Regular' : 'DMSans-Bold')};
  font-size: 16px;
  background-color: #fff;
  border: 1px solid ${p => (p.error ? Colors.error : Colors.formInputBorder)};
  border-radius: 8px;
  padding: 16px;
  flex: 1 1 auto;
  width: 100%;
  margin-top: 12px;
  /* margin-bottom: 1px; */
` as React.FC<
  TextInputProps & {
    hasHelperText?: boolean;
    minHeight?: number;
    multiLine?: boolean;
    ref?: any;
    error?: boolean;
    empty?: boolean;
  }
>;

export interface FormOtherOptionConfig {
  dataSource?: string;
  /** @default 'Other' */
  label?: string;
  // translations?: Translation[];
}

export type DynamicFormMultiSelectConfig = FormOptionsConfig &
  FormGridConfig & {
    placeholder?: string;
    /**
     * Determines if an 'Other' option to enter custom text responses
     * should be displayed at the end of all configured options.
     */
    enableOtherOption?: boolean;
    otherOptionConfig?: FormOtherOptionConfig;
  };

export interface FormSelectOption {
  value: string | number;
  label?: string;
  checked?: boolean;
}

export type FormMultipleChoiceProps<T> = FormElementProps<
  T,
  DynamicFormMultiSelectConfig
> & {
  values?: (string | number | boolean)[];
};

export const DynamicFormMultipleChoice = <T extends object>({
  element,
  values
}: FormMultipleChoiceProps<T>) => {
  const { formValues, setFormValue, entitySchema } = useDynamicFormContext<
    T,
    DynamicFormMultiSelectConfig
  >();
  const isSingleSelect = ['RadioGroup', 'Boolean'].includes(element.type);
  const options = useMemo<FormValueLabel[]>(() => {
    return (element.config?.options || []).map(o =>
      ['string', 'number', 'boolean'].includes(typeof o)
        ? ({ value: o, label: o.toString() } as FormValueLabel)
        : (o as FormValueLabel)
    );
  }, [element.config]);
  const key = (element.dataSource as keyof T) || element.key;
  if (!values) {
    if (isSingleSelect) {
      values = [];
      if (
        formValues[key] ||
        typeof formValues[key] === 'number' ||
        typeof formValues[key] === 'boolean'
      )
        values.push(formValues[key] as any);
    } else {
      values = (formValues[key] || []) as string[];
    }
  }
  // in case the type changes
  if (!Array.isArray(values)) values = [values];
  // "Other" field
  const otherOptionConfig = element.config?.otherOptionConfig || {};
  const otherLabel = otherOptionConfig.label ?? 'Other';
  const otherKey = otherOptionConfig.dataSource as keyof T;
  const otherValue = formValues[otherKey] as string | null | undefined;
  const handlePress = (option: FormValueLabel) => {
    if (!Array.isArray(values)) {
      values = [];
    }
    // don't use strict equality (for number/string compare)
    if (
      !isSingleSelect &&
      values.some(value => value == (option.value as string))
    ) {
      // unselect
      values = values.filter(item => item != option.value);
    } else {
      //select
      if (isSingleSelect) {
        values = [option.value as string];
      } else {
        values = [...values, option.value as string];
      }
    }
    if (isSingleSelect) {
      const column = entitySchema?.columns.find(
        c => c.name === element.dataSource
      );
      if (column?.type === 'integer' || column?.type === 'numeric') {
        setFormValue(key, Number(option.value) as any, element);
      } else {
        setFormValue(key, option.value as any, element);
      }
    } else {
      setFormValue(key, values as any, element);
    }

    if (element.config?.enableOtherOption) {
      const otherSelected = values?.some(value => value == otherLabel);
      if (!otherSelected) {
        setFormValue(otherKey, '' as any, element);
      }
    }
  };
  const [otherFieldFocused, setOtherFieldFocused] = useState<boolean>(false);
  const otherFieldInputRef = useRef<any>(null);
  const isOtherSelected = values?.some(value => value == 'Other');
  // console.log('element.config', element.config);
  return (
    <View>
      <FormElementQuestion element={element} />
      {options.map(option => (
        <StyledTouchableOpacity
          key={option.value.toString()}
          onPress={() => {
            handlePress(option);
            // On Android/iOS, blur the "Other" text field when a different option is clicked
            if (Platform.OS !== 'web') otherFieldInputRef.current?.blur();
          }}
        >
          <StyledContainer>
            {/* don't use strict equality (for number/string compare) */}
            <StyledBox touched={values?.some(value => value == option.value)}>
              {!isSingleSelect &&
                values?.some(value => value == option.value) && (
                  <Feather name='check' size={16} color={Colors.lightText} />
                )}
            </StyledBox>
            {typeof option.label === 'string' ? (
              <StyledB color='dark'>{option.label}</StyledB>
            ) : (
              option.label
            )}
          </StyledContainer>
        </StyledTouchableOpacity>
      ))}
      {element.config?.enableOtherOption && (
        <StyledTouchableOpacity
          onPress={() => {
            // On Android/iOS, blur the text field when the box is clicked
            if (Platform.OS !== 'web') otherFieldInputRef.current?.blur();

            // On web, prevent deselecting the option if the "Other" field is focused
            if (
              Platform.OS !== 'web' ||
              !isOtherSelected ||
              !otherFieldFocused
            ) {
              handlePress({ value: 'Other', label: otherLabel });
            }
          }}
        >
          <StyledContainer direction='column'>
            <StyledRow>
              {/* don't use strict equality (for number/string compare) */}
              <StyledBox touched={isOtherSelected}>
                {!isSingleSelect && isOtherSelected && (
                  <Feather name='check' size={16} color={Colors.lightText} />
                )}
              </StyledBox>
              <StyledOtherLabel color='dark'>{otherLabel}</StyledOtherLabel>
            </StyledRow>
            <StyledTextInput
              ref={otherFieldInputRef}
              onFocus={() => {
                setOtherFieldFocused(true);

                // On Android/iOS, auto-select the "Other" option when the field gains focus
                // to mimic the behavior of the web app.
                if (Platform.OS !== 'web' && !isOtherSelected) {
                  handlePress({ value: 'Other', label: otherLabel });
                }
              }}
              onBlur={() => setOtherFieldFocused(false)}
              blurOnSubmit={false}
              placeholderTextColor={Colors.placeholderColor}
              placeholder='Fill in response'
              value={otherValue || ''}
              empty={!otherValue}
              onChangeText={text => {
                setFormValue(otherKey, text as any, element);
              }}
            />
          </StyledContainer>
        </StyledTouchableOpacity>
      )}
    </View>
  );
};

export const FormMultiSelectPlugin: FormElementPlugin<
  any,
  DynamicFormMultiSelectConfig
> = {
  name: 'Checkboxes',
  type: 'MultiSelect',
  dataTypes: ['text', 'integer', 'numeric', 'enum'],
  dataArray: true,
  element: DynamicFormMultipleChoice,
  validate: validateFormOtherFieldValue
};
